// @flow strict
import { createSelector, type OutputSelector } from 'reselect';

import {
  selectProductsByTypeAndProcess,
  type DecoratedProduct,
} from '@gandi/cart';

import { selectAllResults } from 'src/DomainCreate/modules/selectors/results';

import type { FullState } from '../../types';

type SelectHostingProductArgs = {
  productId?: ?number,
  size?: string,
  application: string | void,
  packId: string,
  min_duration?: number,
};

export const selectCartHasHostingProduct: (
  state: FullState,
  props: SelectHostingProductArgs,
) => ?DecoratedProduct = (state, props) => {
  const products = selectProductsByTypeAndProcess(state, {
    productType: 'paas',
    process: 'create',
  });

  return products.find(
    ({ id, payload }) =>
      (!props.productId || Number(props.productId) === id) &&
      (!props.size || payload.size === props.size) &&
      payload.extra?.application === props.application &&
      (!payload.extra?.packId || payload.extra.packId === props.packId) &&
      (!props.min_duration || payload.duration === props.min_duration),
  );
};

const selectWebhostingProducts: OutputSelector<
  FullState,
  mixed,
  Array<DecoratedProduct>,
> = createSelector([(state: FullState) => state], state =>
  selectProductsByTypeAndProcess(state, {
    productType: 'paas',
    process: 'create',
  }),
);

export const selectFqdnSoldWithHosting: OutputSelector<
  FullState,
  mixed,
  Array<string>,
> = createSelector([selectWebhostingProducts], webhostingProducts => {
  if (!webhostingProducts.length) {
    return [];
  }
  return (
    webhostingProducts
      .filter(product => product.payload?.domain?.fqdn)
      // $FlowFixMe[incompatible-use] - DecoratedProduct is not exact
      .map(product => product.payload.domain.fqdn)
  );
});

export const selectHostingFromFqdn: OutputSelector<
  FullState,
  string,
  DecoratedProduct | void,
> = createSelector(
  [selectWebhostingProducts, (state, fqdn) => fqdn],
  (webhostingProducts, fqdn) =>
    webhostingProducts.find(product => product.payload?.domain?.fqdn === fqdn),
);

export const selectAvailableResultCount: OutputSelector<
  FullState,
  void,
  number,
> = createSelector(
  [selectAllResults],
  results => results.filter(r => r.availability === 'available').length,
);
